import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { STYLE_EMOJI, STYLE_KEYS, type StyleKey } from '@/constants/styles';
import { UNCLASSIFIED_STYLE } from '@/utils/feed';

/**
 * feed-genre-filter — the music-style chips above the feed.
 *
 * The list is CLOSED and ours: it is not Deezer's 23 roots, and not musicmap's
 * 23 super-genres either. From musicmap we kept the discipline of reduction
 * (600 → 230 → 23, explicitly "for comprehensibility"), not its cut — that map
 * spends 8 categories on rock and 1 on rap, and has no classical, no world, no
 * soundtrack: it is an atlas of lineage, and this is a tap.
 *
 * Each key covers one Deezer root, so "unclassified" stays what it is meant to
 * be — a signal that a genre still needs attaching — rather than a dumping
 * ground. Labels live in `translations/*.json` like every other UI string: the
 * UI never shows a raw Deezer label, so the displayed set is closed and small.
 * The emoji carries the chip when the word does not travel (ru/zh/ja).
 */

/**
 * The reset, as a control the SHEET places — on its title row, not in the bar.
 *
 * It lived in the bar twice, and was wrong both times: beside the chips it ate
 * the right edge and cut one mid-word ("🎻 C…"); on its own row it appeared
 * with the first selection and shoved the whole sheet down by a row. The title
 * row has reserved height, so it can come and go without moving anything.
 *
 * LABELLED, because a bare "✕" reads as "close", not "reset the filters".
 * filter-sheet-cleanup — clearing can no longer be undone (the recent-filters
 * history is gone, G 2026-10-12); the label is what keeps it from surprising.
 */
export function StyleFilterClearButton({
  onClear,
  label,
}: {
  onClear: () => void;
  /** Translated label for the reset control. */
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClear}
      data-testid="style-filter-clear"
      className="tw:flex tw:shrink-0 tw:items-center tw:gap-1 tw:rounded-full tw:bg-dark-primary tw:px-3 tw:py-1.5 tw:text-[13px] tw:font-semibold tw:text-grey-2 tw:transition-opacity tw:active:opacity-70"
    >
      <X size={14} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

export default function StyleFilterBar({
  selected,
  onToggle,
  label,
}: {
  selected: readonly string[];
  onToggle: (style: string) => void;
  /** Translated label for a style key — kept out of the component, like AvatarChain's. */
  label: (key: string) => string;
}) {
  const keys: string[] = [...STYLE_KEYS, UNCLASSIFIED_STYLE];

  // Une grille qui passe à la ligne, pas une rangée qui défile : les 16 choix se
  // lisent d'un coup d'oeil. Le défilement horizontal en cachait la moitié hors
  // écran sans rien afficher qui dise qu'on pouvait glisser — et le chip tronqué
  // au bord, seule affordance possible, se lisait comme un bug d'affichage.
  return (
    <div data-testid="style-filter-bar" className="tw:flex tw:flex-wrap tw:gap-2 tw:pb-1">
      {keys.map((key) => {
        const active = selected.includes(key);
        return (
          <button
            key={key}
            type="button"
            aria-pressed={active}
            data-testid={`style-chip-${key}`}
            onClick={() => onToggle(key)}
            className={cn(
              // filter-sheet-cleanup — the dark fill of « Tout effacer », and the
              // Racore-yellow border of the active circle when selected. 2px
              // border at rest too (transparent): selecting never resizes a pill.
              'tw:flex tw:shrink-0 tw:items-center tw:gap-1 tw:rounded-full tw:border-2 tw:bg-dark-primary tw:px-3 tw:py-1.5 tw:text-[13px] tw:transition-opacity tw:active:opacity-70',
              active
                ? 'tw:border-yellow-primary tw:font-semibold tw:text-white'
                : 'tw:border-transparent tw:text-grey-2',
            )}
          >
            <span aria-hidden="true">{STYLE_EMOJI[key as StyleKey]}</span>
            <span>{label(key)}</span>
          </button>
        );
      })}
    </div>
  );
}
